import { Annotation, Lab, Medication, RadiologyReport } from "../../amplify/appsync/query";
import { PatientVisit } from "../../amplify/appsync/search-patients";
import { getLineSegLen, getTimelinePopover, getTimelinePreviewText } from "../../utils/plot";
import { getEarliestTime, getLatestTime, getMinuteTruncatedEpoch } from "../../utils/time";
import { FILTER_WAVEFORM_ANNOTATION_OPTIONS } from "../filters/DashboardFilterOptions";

export const TIMELINE_PLOT_DATA_OFFSET = 4
export const INDEX_EPOCH = 0
export const INDEX_LAB_INDICATOR = 1
export const INDEX_MED_INDICATOR = 2
export const INDEX_ANNOTATION_INDICATOR = 3
export const INDEX_OTHER_INDICATOR = 4
export const INDEX_LAB_METADATA = 5
export const INDEX_MED_METADATA = 6
export const INDEX_ANNOTATION_METADATA = 7
export const INDEX_OTHER_METADATA = 8

/**
 * Parses the labs and medication data into a format used by the uPlot
 * @param currPatientVisit The patient visit
 * @param labs An array of objects that look like the following:
 *  // {
    //     "acquisition_time": 1578905220,
    //     "component_value": 1.2,
    //     "component_unit": "mg/dL",
    //     "component_result": "",
    //     "lab_group": "Metabolic",
    //     "lab_name": "Creatinine",
    //     "normal_range_low": 0.8,
    //     "normal_range_high": 2,
    //     "status": ""
    // }
 * @param meds An array of objects that look like the following:
    // {
    //     "action_time": 1578905220,
    //     "end_time": 1578905220,
    //     "action": "Given",
    //     "dose": 100,
    //     "dose_unit": "MCG/ML",
    //     "med_name": "PHENYLEPHRINE HCL IN 0.9% NACL 0.5 MG/5 ML (100 MCG/ML) IV SYRG"
    // }
 * @returns The plot data
 */
export function parseDataForTimelinePlot(currPatientVisit: PatientVisit, labs: Lab[], meds: Medication[], annotations: Annotation[], tenant?: string) {
    console.log("parseDataForTimelinePlot")
    //
    // Interweave the labs and meds onto the same plot
    //
    // Create the segments for each lab and med:
    // {
    //      <Start epoch time of segment>: [
    //          [
    //              Start epoch time of segment,
    //              End epoch time of segment,
    //              Name of med or lab
    //              Offset (to prevent overlap of meds that happen at the same time - only applies to the meds)
    //          ] 
    //      ]
    // }
    // 
    //

    let start = getMinuteTruncatedEpoch(getEarliestTime(currPatientVisit) / 1000);
    let end = getMinuteTruncatedEpoch(getLatestTime(currPatientVisit, tenant) / 1000);

    // Epoch Timestamps, Labs, Meds, Annotations
    const plotData: any = [[], [], [], [], [], [], [], [], []];
    let labSegments: any = {};
    let medSegments: any = {};
    let annotationSegments: any = {};
    let otherSegments: any = {};
    for (let lab of labs) {
        const acquisition_time = getMinuteTruncatedEpoch(lab.acquisition_time);
        if (!(acquisition_time in labSegments)) {
            labSegments[acquisition_time] = []
        }
        labSegments[acquisition_time].push([
            getMinuteTruncatedEpoch(lab.acquisition_time),
            getMinuteTruncatedEpoch(lab.acquisition_time),
            lab.lab_name,
        ]);
    }
    for (let med of meds) {
        // Filter out any unwanted entries
        if (med.action === "Canceled Entry") {
            continue;
        }

        const action_time = getMinuteTruncatedEpoch(new Date(med.action_time).getTime() / 1000);
        if (!(action_time in medSegments)) {
            medSegments[action_time] = []
        }
        medSegments[action_time].push([
            getMinuteTruncatedEpoch(new Date(med.action_time).getTime() / 1000),
            med.end_time ? getMinuteTruncatedEpoch(new Date(med.end_time).getTime() / 1000) : getMinuteTruncatedEpoch(new Date(med.action_time).getTime() / 1000),
            `${med.med_name} <br />Dose: ${med.dose} ${med.dose_unit} <br />Action: (${med.action})`,
        ]);
    }
    for (let annotation of annotations) {
        if (annotation.annotation_source === "USER_ANNOTATION") {
            const time = getMinuteTruncatedEpoch(annotation.annotation_time);
            if (!(time in annotationSegments)) {
                annotationSegments[time] = []
            }
            annotationSegments[time].push([
                time,
                getMinuteTruncatedEpoch(annotation.annotation_time + annotation.annotation_length_seconds),
                annotation.measure_name,
            ]);
        }

        if (annotation.annotation_source === "RADIOLOGY_REPORT") {
            const time = getMinuteTruncatedEpoch(annotation.annotation_time);
            if (!(time in otherSegments)) {
                otherSegments[time] = []
            }
            otherSegments[time].push([
                time,
                getMinuteTruncatedEpoch(annotation.annotation_time + annotation.annotation_length_seconds),
                annotation.measure_name,
            ]);
        }
    }

    //
    // plotData:
    // [
    //      epochs,
    //      lab vals,
    //      med vals,
    //      annotations vals,
    //      [# of lab vals, text preview, line segment length in sec],
    //      [# of med vals, text preview, line segment length in sec],
    //      [# of annotations vals, text preview, line segment length in sec],
    // ]
    //
    let curr = start;

    let ringIndex = 0;

    while (curr <= end) {
        let dataAdded = false;
        if (curr in medSegments) {
            if (curr in labSegments) {
                // Med and Lab segments
                medSegments[curr].forEach((seg: any, idx: number) => {
                    // Offset each medication by one second, to help with plotting
                    // This assumes that we have at most 60 medications provided in one minute
                    // which seems like a reasonable assumption
                    if (idx >= 59) {
                        console.warn("There are more than 60 medications in a minute - not plotting the rest.")
                        return;
                    }

                    if (idx === 0) {
                        // Only plot the labs on the whole minute
                        plotData[INDEX_EPOCH].push(curr + idx);
                        plotData[INDEX_LAB_INDICATOR].push(0);
                        plotData[INDEX_MED_INDICATOR].push(2 - (ringIndex % 4) * 0.5); // Offset between 0.25 - 2 to avoid overcrowding on the plot. Valid values: [2, 1.5, 1, 0.5, 2, 1.5, ...]
                        plotData[INDEX_ANNOTATION_INDICATOR].push(null);
                        plotData[INDEX_OTHER_INDICATOR].push(null);
                        plotData[INDEX_LAB_METADATA].push([labSegments[curr].length, "", 0, getTimelinePopover(labSegments[curr], "labs collected")]);
                        plotData[INDEX_MED_METADATA].push([0, getTimelinePreviewText([seg]), getLineSegLen(seg), getTimelinePopover([seg], "medications")]);
                        plotData[INDEX_ANNOTATION_METADATA].push(null);
                        plotData[INDEX_OTHER_METADATA].push(null);
                        dataAdded = true;
                        ringIndex++;
                    } else {
                        plotData[INDEX_EPOCH].push(curr + idx);
                        plotData[INDEX_LAB_INDICATOR].push(null);
                        plotData[INDEX_MED_INDICATOR].push(2 - (ringIndex % 4) * 0.5);
                        plotData[INDEX_ANNOTATION_INDICATOR].push(null);
                        plotData[INDEX_OTHER_INDICATOR].push(null);
                        plotData[INDEX_LAB_METADATA].push(null);
                        plotData[INDEX_MED_METADATA].push([0, getTimelinePreviewText([seg]), getLineSegLen(seg), getTimelinePopover([seg], "medications")]);
                        plotData[INDEX_ANNOTATION_METADATA].push(null);
                        plotData[INDEX_OTHER_METADATA].push(null);
                        dataAdded = true;
                        ringIndex++;
                    }
                });
            } else {
                // Med segments only
                medSegments[curr].forEach((seg: any, idx: number) => {
                    // Offset each medication by one second, to help with plotting
                    // This assumes that we have at most 60 medications provided in one minute
                    // which seems like a reasonable assumption
                    if (idx >= 59) {
                        console.warn("There are more than 60 medications in a minute - not plotting the rest.")
                        return;
                    }

                    plotData[INDEX_EPOCH].push(curr + idx);
                    plotData[INDEX_LAB_INDICATOR].push(null);
                    plotData[INDEX_MED_INDICATOR].push(2 - (ringIndex % 4) * 0.5);
                    plotData[INDEX_ANNOTATION_INDICATOR].push(null);
                    plotData[INDEX_OTHER_INDICATOR].push(null);
                    plotData[INDEX_LAB_METADATA].push(null);
                    plotData[INDEX_MED_METADATA].push([medSegments[curr].length, getTimelinePreviewText([seg]), getLineSegLen(seg), getTimelinePopover([seg], "medications")]);
                    plotData[INDEX_ANNOTATION_METADATA].push(null);
                    plotData[INDEX_OTHER_METADATA].push(null);
                    dataAdded = true;

                    ringIndex++;
                });
            }
        } else if (curr in labSegments) {
            // Lab segments only
            plotData[INDEX_EPOCH].push(curr);
            plotData[INDEX_LAB_INDICATOR].push(0);
            plotData[INDEX_MED_INDICATOR].push(null);
            plotData[INDEX_ANNOTATION_INDICATOR].push(null);
            plotData[INDEX_OTHER_INDICATOR].push(null);
            plotData[INDEX_LAB_METADATA].push([labSegments[curr].length, "", 0, getTimelinePopover(labSegments[curr], "labs collected")]);
            plotData[INDEX_MED_METADATA].push(null);
            plotData[INDEX_ANNOTATION_METADATA].push(null);
            plotData[INDEX_OTHER_METADATA].push(null);
            dataAdded = true;
        }

        if (curr in annotationSegments) {
            const metadata = [annotationSegments[curr].length, getTimelinePreviewText(annotationSegments[curr]), 0, getTimelinePopover(annotationSegments[curr], "")];
            if (dataAdded) {
                let idx = plotData[INDEX_EPOCH].length - 1;
                plotData[INDEX_ANNOTATION_INDICATOR][idx] = 2 - (ringIndex % 4) * 0.5
                plotData[INDEX_ANNOTATION_METADATA][idx] = metadata
                ringIndex++;
            } else {
                // Annotation segments only for this time
                plotData[INDEX_EPOCH].push(curr);
                plotData[INDEX_LAB_INDICATOR].push(null);
                plotData[INDEX_MED_INDICATOR].push(null);
                plotData[INDEX_ANNOTATION_INDICATOR].push(2 - (ringIndex % 4) * 0.5);
                plotData[INDEX_OTHER_INDICATOR].push(null);
                plotData[INDEX_LAB_METADATA].push(null);
                plotData[INDEX_MED_METADATA].push(null);
                plotData[INDEX_ANNOTATION_METADATA].push(metadata);
                plotData[INDEX_OTHER_METADATA].push(null);
                ringIndex++;
            }
        }

        if (curr in otherSegments) {
            const metadata = [otherSegments[curr].length, "", 0, getTimelinePopover(otherSegments[curr], "imaging")];
            if (dataAdded) {
                let idx = plotData[INDEX_EPOCH].length - 1;
                plotData[INDEX_OTHER_INDICATOR][idx] = 0
                plotData[INDEX_OTHER_METADATA][idx] = metadata
            } else {
                // Other segments only for this time
                plotData[INDEX_EPOCH].push(curr);
                plotData[INDEX_LAB_INDICATOR].push(null);
                plotData[INDEX_MED_INDICATOR].push(null);
                plotData[INDEX_ANNOTATION_INDICATOR].push(null);
                plotData[INDEX_OTHER_INDICATOR].push(0);
                plotData[INDEX_LAB_METADATA].push(null);
                plotData[INDEX_MED_METADATA].push(null);
                plotData[INDEX_ANNOTATION_METADATA].push(null);
                plotData[INDEX_OTHER_METADATA].push(metadata);
            }
        }

        curr += 60;
    }

    // Ensure empty bounds are added for plotting purposes
    plotData[INDEX_EPOCH].unshift(start);
    plotData[INDEX_LAB_INDICATOR].unshift(null);
    plotData[INDEX_MED_INDICATOR].unshift(null);
    plotData[INDEX_ANNOTATION_INDICATOR].unshift(null);
    plotData[INDEX_OTHER_INDICATOR].unshift(null);
    plotData[INDEX_LAB_METADATA].unshift(null);
    plotData[INDEX_MED_METADATA].unshift(null);
    plotData[INDEX_ANNOTATION_METADATA].unshift(null);
    plotData[INDEX_OTHER_METADATA].unshift(null);
    plotData[INDEX_EPOCH].push(end);
    plotData[INDEX_LAB_INDICATOR].push(null);
    plotData[INDEX_MED_INDICATOR].push(null);
    plotData[INDEX_ANNOTATION_INDICATOR].push(null);
    plotData[INDEX_OTHER_INDICATOR].push(null);
    plotData[INDEX_LAB_METADATA].push(null);
    plotData[INDEX_MED_METADATA].push(null);
    plotData[INDEX_ANNOTATION_METADATA].push(null);
    plotData[INDEX_OTHER_METADATA].push(null);

    return plotData;
}

/**
 * Groups the lab data by lab group to render on the labs modal
 * @param labs An array of lab data returned from tne query
 * @return The lab data grouped by lab group
 */
export function parseLabsDataForModal(labs: Lab[]) {
    const map = new Map<string, Lab[]>();
    const keySet = new Set<string>();
    for (let lab of labs) {
        if (!map.has(lab.lab_group)) {
            map.set(lab.lab_group, []);
        }
        map.get(lab.lab_group)?.push(lab);
        keySet.add(lab.lab_group);
    }

    const output: Lab[][] = [];

    const keys = Array.from(keySet);
    keys.sort();
    for (let key of keys) {
        output.push(map.get(key)!);
    }
    return output;
}

export function parseWaveformAnnotations(annotations: Annotation[], currPatientVisit: PatientVisit, uniqueECGClassifications: any[], filterWaveformOptions: any, tenant?: string) {
    const waveformAvailability: any = [[], [], [], [], [], [], [], [], []];

    const annotationTimes = [];
    const annotationTimeToVals: any = {};
    let idx = 0;
    for (let annotation of annotations) {
        if (!(annotation.annotation_time in annotationTimeToVals)) {
            annotationTimes.push(annotation.annotation_time);
            annotationTimeToVals[annotation.annotation_time] = [null, null, null, null, null, null, null, null];
        }
        if (annotation.annotation_source == "WAVEFORM_QUALITY" && annotation.annotation_target === "II") {
            annotationTimeToVals[annotation.annotation_time][0] = annotation.measure_value > 0 ? 2 : null;
            annotationTimeToVals[annotation.annotation_time][1] = annotation.measure_value === 0 ? 2 : null;
        } else if (annotation.annotation_source == "WAVEFORM_QUALITY" && annotation.annotation_target === "Pleth") {
            annotationTimeToVals[annotation.annotation_time][2] = annotation.measure_value > 0 ? 1 : null;
            annotationTimeToVals[annotation.annotation_time][3] = annotation.measure_value === 0 ? 1 : null;
        } else if (annotation.annotation_source == "WAVEFORM_QUALITY" && annotation.annotation_target === "Resp") {
            annotationTimeToVals[annotation.annotation_time][4] = annotation.measure_value > 0 ? 0 : null;
            annotationTimeToVals[annotation.annotation_time][5] = annotation.measure_value === 0 ? 0 : null;
        } else if (annotation.annotation_source == "ECG_CLASSIFICATION" && annotation.annotation_target === "II") {
            const waveformAnnotationId = uniqueECGClassifications.length > 0 ? uniqueECGClassifications[filterWaveformOptions["waveformAnnotation"]].id : "Non-Sinus";
            annotationTimeToVals[annotation.annotation_time][7] = annotation.measure_name;
            if (waveformAnnotationId === "Non-Sinus") {
                // The "Non-Sinus" option will highlight all annotations that do not start with the word "Sinus"
                if (!annotation.measure_name.startsWith("Sinus")) {
                    annotationTimeToVals[annotation.annotation_time][6] = 2;
                } else {
                    annotationTimeToVals[annotation.annotation_time][6] = null;
                }
            } else if (waveformAnnotationId === "None") {
                annotationTimeToVals[annotation.annotation_time][6] = null;
            } else {
                if (annotation.measure_name === waveformAnnotationId) {
                    annotationTimeToVals[annotation.annotation_time][6] = 2;
                } else {
                    annotationTimeToVals[annotation.annotation_time][6] = null;
                }
            }
        }
        idx++;
    }

    for (let annotationTime of annotationTimes) {
        waveformAvailability[0].push(annotationTime);
        waveformAvailability[1].push(annotationTimeToVals[annotationTime][0]);
        waveformAvailability[2].push(annotationTimeToVals[annotationTime][1]);
        waveformAvailability[3].push(annotationTimeToVals[annotationTime][2]);
        waveformAvailability[4].push(annotationTimeToVals[annotationTime][3]);
        waveformAvailability[5].push(annotationTimeToVals[annotationTime][4]);
        waveformAvailability[6].push(annotationTimeToVals[annotationTime][5]);
        waveformAvailability[7].push(annotationTimeToVals[annotationTime][6]);
        waveformAvailability[8].push(annotationTimeToVals[annotationTime][7]);
    }

    let start = getMinuteTruncatedEpoch(getEarliestTime(currPatientVisit) / 1000);
    let end = getMinuteTruncatedEpoch(getLatestTime(currPatientVisit, tenant) / 1000);
    waveformAvailability[0].unshift(start);
    waveformAvailability[1].unshift(null);
    waveformAvailability[2].unshift(null);
    waveformAvailability[3].unshift(null);
    waveformAvailability[4].unshift(null);
    waveformAvailability[5].unshift(null);
    waveformAvailability[6].unshift(null);
    waveformAvailability[7].unshift(null);
    waveformAvailability[8].unshift(null);
    waveformAvailability[0].push(end);
    waveformAvailability[1].push(null);
    waveformAvailability[2].push(null);
    waveformAvailability[3].push(null);
    waveformAvailability[4].push(null);
    waveformAvailability[5].push(null);
    waveformAvailability[6].push(null);
    waveformAvailability[7].push(null);
    waveformAvailability[8].push(null);

    return waveformAvailability;
}

export function convertRadiologyReportToAnnotation(radiologyReports: RadiologyReport[]): Annotation[] {
    const annotations: Annotation[] = [];
    for (let radiologyReport of radiologyReports) {
        annotations.push({
            annotation_id: radiologyReport.imaging_id,
            annotation_time: radiologyReport.result_time,
            annotation_source: "RADIOLOGY_REPORT",
            annotation_target: "RADIOLOGY_REPORT",
            annotation_length_seconds: 60,
            measure_name: radiologyReport.study,
            measure_value: radiologyReport.imaging_id,
        })
    }
    return annotations;
}

export function parseUniqueECGClassifications(annotations: Annotation[], filterWaveformOptions: any) {
    const classifications: any = new Set<string>();
    classifications.add("Non-Sinus");
    classifications.add("None");

    for (let annotation of annotations) {
        if (annotation.annotation_source == "ECG_CLASSIFICATION" && annotation.annotation_target === "II") {
            classifications.add(annotation.measure_name);
        }
    }

    const output = [...FILTER_WAVEFORM_ANNOTATION_OPTIONS.filter((opt: any) => classifications.has(opt.id))];
    return output;
}

export function parseECGNumerics(annotations: Annotation[], currPatientVisit: PatientVisit, tenant?: string) {
    // {
    //      "QTc": [
    //          [1600883520, 1600883521, ...],
    //          [1, 2, ...], // Charted #1
    //          [1, 2, ...], // Charted #2
    //          [1, 2, ...], // Monitor #1
    //          [1, 2, ...], // Monitor #2
    //          [1, 2, ...], // ABNORMAL Charted #1
    //          [1, 2, ...], // ABNORMAL Charted #2
    //          [1, 2, ...], // ABNORMAL Monitor #1
    //          [1, 2, ...], // ABNORMAL Monitor #2
    //      ]
    // }

    let start = getMinuteTruncatedEpoch(getEarliestTime(currPatientVisit) / 1000);
    let end = getMinuteTruncatedEpoch(getLatestTime(currPatientVisit, tenant) / 1000);

    const data: any = {};

    for (let annotation of annotations) {
        if (annotation.annotation_source == "ECG_NUMERIC" && annotation.annotation_target === "II") {
            if (!(annotation.measure_name in data)) {
                data[annotation.measure_name] = [[], [], [], [], [], [], [], [], []];
            }
            data[annotation.measure_name][0].push(annotation.annotation_time);
            data[annotation.measure_name][1].push(null);
            data[annotation.measure_name][2].push(null);
            data[annotation.measure_name][3].push(annotation.measure_value);
            data[annotation.measure_name][4].push(null);
            data[annotation.measure_name][5].push(null);
            data[annotation.measure_name][6].push(null);
            data[annotation.measure_name][7].push(null);
            data[annotation.measure_name][8].push(null);
        }
    }

    for (let measure in data) {
        // Ensure empty bounds are added for plotting purposes
        data[measure][0].unshift(start);
        data[measure][1].unshift(null);
        data[measure][2].unshift(null);
        data[measure][3].unshift(null);
        data[measure][4].unshift(null);
        data[measure][5].unshift(null);
        data[measure][6].unshift(null);
        data[measure][7].unshift(null);
        data[measure][8].unshift(null);
        data[measure][0].push(end);
        data[measure][1].push(null);
        data[measure][2].push(null);
        data[measure][3].push(null);
        data[measure][4].push(null);
        data[measure][5].push(null);
        data[measure][6].push(null);
        data[measure][7].push(null);
        data[measure][8].push(null);
    }

    return data;
}
